import {
  BonusConfiguration,
  BonusContext,
  BonusScene,
  INumberFormatter,
  NUMBER_FORMATTER_TOKEN,
  SelectAction,
  SelectionArgs,
} from '@cgs/common';
import { CancellationTokenSource } from '@cgs/shared';
import {
  IBonusGameOptions,
  NewBonusGameProvider,
  RESOURCES_COMPONENT_TOKEN,
  ResourcesComponent,
  T_IStorageRepositoryProvider,
} from '@cgs/machines';
import { TRIFECTA_LAUNCH_PARAMS_TOKEN, TrifectaLaunchParamsDto } from '@cgs/provider-trifecta';
import { Container, EventStreamSubscription } from '@cgs/wge';
import { CorsicaBonusGame } from './corsica-bonus-game';

export class CorsicaBonusGameProvider extends NewBonusGameProvider {
  private selectSub: EventStreamSubscription<SelectionArgs>;
  private autoClickSub: EventStreamSubscription<void>;
  private isReplay: boolean = false;
  constructor(container: Container, options: IBonusGameOptions) {
    super(container, options);

    const launchParams = this.container.resolve<TrifectaLaunchParamsDto>(
      TRIFECTA_LAUNCH_PARAMS_TOKEN
    );
    this.isReplay = launchParams.replayData !== undefined;
  }

  async startBonus(): Promise<void> {
    if (
      this.bonusResponse &&
      (!this.gameTypes || this.gameTypes.includes(this.bonusResponse.type))
    ) {
      this.cancellationTokenSource = new CancellationTokenSource();

      const bonusHolder =
        this.container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN).bonusHolder;
      this.bonusHolder = bonusHolder;

      const bonusId = this.gameTypeToBonusId.has(this.bonusResponse.type)
        ? this.gameTypeToBonusId.get(this.bonusResponse.type)!
        : '';

      const config = await (this as any).configManager.getConfig(bonusId);

      if (!config) {
        throw new Error(`Failed to load bonus config for bonusId: ${bonusId}`);
      }

      const configuration = BonusConfiguration.fromJson(
        config,
        this.container.resolve<INumberFormatter>(NUMBER_FORMATTER_TOKEN)
      );

      const bonusContext = new BonusContext();
      bonusContext.storageRepositoryProvider = this.container.resolve(T_IStorageRepositoryProvider);

      const root = this.container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN).root;

      const externalScenes = this.externalScenesIds
        ? this.externalScenesIds.map((id) => root.findById(id)!)
        : null;

      this.bonusScene = new BonusScene(
        this.bonusNode,
        configuration,
        this.resourceManager,
        bonusContext,
        externalScenes,
        this.cancellationTokenSource.token,
        this
      );

      this.bonusGame = new CorsicaBonusGame(
        this.container,
        this.bonusScene,
        configuration,
        bonusContext,
        this.updateBonusFinishPropertiesWithCurrent(),
        this.bonusResponse.type,
        this.cancellationTokenSource.token
      );

      this.miniGameCreatedDispatcher.dispatchEvent(this.bonusGame);

      this.serverException = this.bonusGame.onServerException.listen(() => {
        this.serverException.cancel();
        this.onServerExceptionInternal();
      });

      this.finishingSub = this.bonusScene.bonusFinishing.listen(() => {
        this.finishingSub.cancel();
        this.onMiniGameFinishingInternal();
      });

      this.shownSub = this.bonusScene.bonusShown.listen(() => {
        this.shownSub.cancel();
        this.onMiniGameStartedInternal();
      });

      this.finishedSub = this.bonusScene.bonusFinished.listen(() => {
        this.finishedSub.cancel();
        this.onMiniGameFinishedInternal();
      });

      this.selectSub = this.bonusScene.select.listen((e) => {
        (this.bonusGame as CorsicaBonusGame).onSelectButton(e.selectedIndex!);
      });

      this.updatedSub = this.bonusScene!.bonusUpdated.listen(() => {
        this.onMiniGameUpdatedInternal();
      });

      this.bonusGame.start(this.bonusResponse);

      if (this.isReplay) {
        this.replayBonus();
      }
    }
  }

  public replayBonus(): void {
    this.autoClickSub = this.bonusScene!.roundShown.listen(() => {
      const roundIndex = this.bonusScene!.roundIndex();
      if (roundIndex === 0) {
        const selectionArgs: SelectionArgs = {
          selectAction: SelectAction.FinishRound,
          selectedIndex: 0,
          serverIndex: 0,
          buttonsSetName: 'buttons',
          sendValue: null,
        };

        setTimeout(() => {
          (this.bonusScene as BonusScene).onSelect(selectionArgs);
        }, 100);
      }

      if (roundIndex === 1) {
        const selectionArgs: SelectionArgs = {
          selectAction: SelectAction.SendIndex,
          selectedIndex: Math.floor(Math.random() * 3),
          serverIndex: 0,
          buttonsSetName: 'buttons',
          sendValue: null,
        };

        setTimeout(() => {
          (this.bonusGame as CorsicaBonusGame).onSelectButton(selectionArgs.selectedIndex!);
          (this.bonusGame as CorsicaBonusGame).sendToServer(selectionArgs);
        }, 100);
      }

      if (roundIndex === 2 || roundIndex === 3) {
        const selectionArgs: SelectionArgs = {
          selectAction: SelectAction.SendIndex,
          selectedIndex: Math.floor(Math.random() * 2),
          serverIndex: roundIndex - 1,
          buttonsSetName: 'buttons',
          sendValue: null,
        };

        setTimeout(() => {
          (this.bonusGame as CorsicaBonusGame).onSelectButton(selectionArgs.selectedIndex!);
          (this.bonusGame as CorsicaBonusGame).sendToServer(selectionArgs);
        }, 100);
      }
    });
  }

  public dispose(): void {
    this.selectSub.cancel();

    if (this.isReplay) {
      this.autoClickSub.cancel();
    }
    super.dispose();
  }
}
